import { createContext, useContext, useState, useEffect, useCallback, type ReactNode } from 'react';
import { getAuthorizationUrl, refreshAccessToken } from './auth-config';

interface AuthUser {
  id: string;
  email: string;
  name: string;
}

interface AuthContextType {
  user: AuthUser | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  login: () => void;
  logout: () => void;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

function parseJwtPayload(token: string): Record<string, unknown> | null {
  try {
    const base64 = token.split('.')[1].replace(/-/g, '+').replace(/_/g, '/');
    return JSON.parse(atob(base64));
  } catch {
    return null;
  }
}

function isTokenExpired(token: string): boolean {
  const payload = parseJwtPayload(token);
  if (!payload || typeof payload.exp !== 'number') return true;
  return Date.now() >= (payload.exp - 30) * 1000;
}

function extractUser(token: string): AuthUser | null {
  const payload = parseJwtPayload(token);
  if (!payload) return null;
  return {
    id: (payload.sub as string) ?? '',
    email: ((payload.emails as string[])?.[0]) ?? (payload.email as string) ?? '',
    name: (payload.name as string) ?? (payload.given_name as string) ?? '',
  };
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<AuthUser | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  const clearAuth = useCallback(() => {
    sessionStorage.removeItem('access_token');
    sessionStorage.removeItem('refresh_token');
    sessionStorage.removeItem('id_token');
    setUser(null);
  }, []);

  useEffect(() => {
    const token = sessionStorage.getItem('access_token');
    if (token && !isTokenExpired(token)) {
      const idToken = sessionStorage.getItem('id_token');
      setUser(extractUser(idToken ?? token));
    } else if (token) {
      const rt = sessionStorage.getItem('refresh_token');
      if (rt) {
        refreshAccessToken(rt)
          .then((res) => {
            sessionStorage.setItem('access_token', res.accessToken);
            sessionStorage.setItem('refresh_token', res.refreshToken);
            if (res.idToken) sessionStorage.setItem('id_token', res.idToken);
            setUser(extractUser(res.idToken ?? res.accessToken));
          })
          .catch(() => clearAuth());
      } else {
        clearAuth();
      }
    }
    setIsLoading(false);
  }, [clearAuth]);

  function login() {
    window.location.href = getAuthorizationUrl();
  }

  function logout() {
    clearAuth();
    window.location.href = '/login';
  }

  const isAuthenticated = user !== null;

  return (
    <AuthContext.Provider value={{ user, isAuthenticated, isLoading, login, logout }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) throw new Error('useAuth must be used within AuthProvider');
  return context;
}
